/**
 * Filter configuration manager
 */

import { FilterOptions } from './types';

/**
 * Manages transaction filter configuration
 */
export class FilterManager {
  private filters: FilterOptions = {};

  /**
   * Set new filters (replaces existing filters)
   * @param filters - Filter options
   */
  setFilters(filters: FilterOptions): void {
    // Normalize addresses to lowercase
    const normalizedFilters: FilterOptions = {};
    
    if (filters.addresses) {
      normalizedFilters.addresses = filters.addresses.map(addr => addr.toLowerCase());
    }
    
    if (filters.methods) {
      normalizedFilters.methods = filters.methods.map(method => method.toLowerCase());
    }
    
    this.filters = normalizedFilters;
  }

  /**
   * Add addresses to the filter
   * @param addresses - Array of addresses to add
   */
  addAddresses(addresses: string[]): void {
    if (!this.filters.addresses) {
      this.filters.addresses = [];
    }
    
    const normalizedAddresses = addresses.map(addr => addr.toLowerCase());
    this.filters.addresses.push(...normalizedAddresses);
    
    // Remove duplicates
    this.filters.addresses = Array.from(new Set(this.filters.addresses));
  }

  /**
   * Remove addresses from the filter
   * @param addresses - Array of addresses to remove
   */
  removeAddresses(addresses: string[]): void {
    if (!this.filters.addresses) {
      return;
    }
    
    const normalizedAddresses = new Set(addresses.map(addr => addr.toLowerCase()));
    this.filters.addresses = this.filters.addresses.filter(
      addr => !normalizedAddresses.has(addr)
    );
  }

  /**
   * Add methods to the filter
   * @param methods - Array of method signatures to add
   */
  addMethods(methods: string[]): void {
    if (!this.filters.methods) {
      this.filters.methods = [];
    }
    
    const normalizedMethods = methods.map(method => method.toLowerCase());
    this.filters.methods.push(...normalizedMethods);
    
    // Remove duplicates
    this.filters.methods = Array.from(new Set(this.filters.methods));
  }

  /**
   * Remove methods from the filter
   * @param methods - Array of method signatures to remove
   */
  removeMethods(methods: string[]): void {
    if (!this.filters.methods) {
      return;
    }
    
    const normalizedMethods = new Set(methods.map(method => method.toLowerCase()));
    this.filters.methods = this.filters.methods.filter(
      method => !normalizedMethods.has(method)
    );
  }

  /**
   * Get current filters
   * @returns Current filter options
   */
  getFilters(): FilterOptions {
    return { ...this.filters };
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.filters = {};
  }

  /**
   * Check if any filters are set
   * @returns Whether any filters are configured
   */
  hasFilters(): boolean {
    return !!(
      (this.filters.addresses && this.filters.addresses.length > 0) ||
      (this.filters.methods && this.filters.methods.length > 0)
    );
  }

  /**
   * Check if filters are empty
   * @returns Whether no filters are configured
   */
  isEmpty(): boolean {
    return !this.hasFilters();
  }

  /**
   * Serialize filters to URL query parameters
   * @returns URL search params string
   */
  toQueryParams(): URLSearchParams {
    const params = new URLSearchParams();
    
    if (this.filters.addresses && this.filters.addresses.length > 0) {
      this.filters.addresses.forEach(addr => params.append('addresses', addr));
    }
    
    if (this.filters.methods && this.filters.methods.length > 0) {
      this.filters.methods.forEach(method => params.append('methods', method));
    }
    
    return params;
  }

  /**
   * Serialize filters to JSON for WebSocket messages
   * @returns JSON-serializable filter object
   */
  toJSON(): FilterOptions {
    return this.getFilters();
  }
}

